
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CreditCard, DollarSign, User, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';

const PaymentProcessing = () => {
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);

  const pendingPayments = [
    {
      id: '1',
      patient: 'Sarah Johnson',
      amount: 450,
      type: 'treatment',
      service: 'Botox Treatment',
      date: '2024-01-20',
      status: 'pending',
      method: 'credit_card'
    },
    {
      id: '2', 
      patient: 'Mike Chen',
      amount: 200,
      type: 'membership',
      service: 'Monthly Membership',
      date: '2024-01-20',
      status: 'failed',
      method: 'autopay'
    },
    {
      id: '3',
      patient: 'Lisa Wong',
      amount: 650,
      type: 'package',
      service: 'Treatment Package',
      date: '2024-01-19',
      status: 'processing',
      method: 'credit_card'
    }
  ];

  const recentPayments = [
    {
      id: '4',
      patient: 'Emma Davis',
      amount: 300,
      type: 'treatment',
      service: 'HydraFacial',
      date: '2024-01-19',
      status: 'completed',
      method: 'credit_card'
    },
    {
      id: '5',
      patient: 'Alex Rodriguez',
      amount: 150,
      type: 'credits',
      service: 'JUV Credits',
      date: '2024-01-18',
      status: 'completed',
      method: 'debit_card'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-juv-soft-blush text-juv-deep-chestnut';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'failed': return <XCircle className="h-4 w-4" />;
      case 'processing': 
      case 'pending': return <Clock className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const handlePaymentAction = (paymentId: string, action: string) => {
    console.log(`${action} payment ${paymentId}`);
    // Implement payment processing logic
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
              Payment Processing
            </h1>
            <p className="mt-1 text-juv-deep-chestnut/70">
              Manage payments and transactions
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-juv-soft-blush flex items-center justify-center mr-4">
                    <Clock className="h-6 w-6 text-juv-deep-chestnut" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pending Payments</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">
                      ${pendingPayments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Today's Revenue</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">$2,450</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Failed Payments</p>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">2</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Payments */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardHeader>
              <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                <Clock className="h-5 w-5 mr-2 text-juv-terracotta" />
                Pending Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 rounded-lg border border-juv-rose-clay/20">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <h4 className="font-medium text-juv-deep-chestnut">{payment.patient}</h4>
                        <Badge className={`${getStatusColor(payment.status)} flex items-center space-x-1`}>
                          {getStatusIcon(payment.status)}
                          <span>{payment.status}</span>
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Amount:</span> ${payment.amount}
                        </div>
                        <div>
                          <span className="font-medium">Service:</span> {payment.service}
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {new Date(payment.date).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">Method:</span> {payment.method.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {payment.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePaymentAction(payment.id, 'approve')}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handlePaymentAction(payment.id, 'decline')}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                      {payment.status === 'failed' && (
                        <Button
                          size="sm"
                          className="bg-juv-terracotta text-white"
                          onClick={() => handlePaymentAction(payment.id, 'retry')}
                        >
                          Retry Payment
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardHeader>
              <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 rounded-lg bg-green-50">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <h4 className="font-medium text-juv-deep-chestnut">{payment.patient}</h4>
                        <Badge className={getStatusColor(payment.status)}>
                          {payment.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Amount:</span> ${payment.amount}
                        </div>
                        <div>
                          <span className="font-medium">Service:</span> {payment.service}
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {new Date(payment.date).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">Method:</span> {payment.method.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      View Receipt
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessing;
